import { useMemo } from 'react'
import { useQuery } from 'react-query'
import settings from '../settings'
import { appBaseUrl } from '../../../utils/appBaseUrl'

const url = `${settings.badaAppointmentApiUrl}/cms/page?contentUrl=${appBaseUrl}&brand=${settings.brand}`

const useContent = () =>
  useQuery(['cms-content'], () => fetch(url).then((r) => r.json()), {
    retry: 2,
  })
export const useCmsContent = () => {
  const { data, isLoading } = useContent()

  const services = useMemo(
    () =>
      data?.meetingTypes?.map((meeting) => ({
        id: meeting.serviceId,
        code: meeting.serviceCode,
        name: meeting.name,
        heading: meeting.heading,
        type: meeting.serviceType,
        serviceCode: meeting.serviceCode,
        serviceId: `${meeting.serviceId || ''}`,
        categoryId: meeting.categoryId,
        subsystem: meeting.subsystem,
        duration: meeting.duration,
        serviceType: meeting.serviceType,
        visibility: meeting.visibility,
        icon: meeting.icon,
        usePrepform: meeting.usePrepform,
        descriptionDesktop: meeting.descriptionDesktop,
        descriptionMobile: meeting.descriptionMobile,
        slotSelectionHeadingDesktop: meeting.slotSelectionHeadingDesktop,
        slotSelectionHeadingMobile: meeting.slotSelectionHeadingMobile,
        slotSelectionTextDesktop: meeting.slotSelectionTextDesktop,
        slotSelectionTextMobile: meeting.slotSelectionTextMobile,
        slotSelectionNoteDesktop: meeting.slotSelectionNoteDesktop,
        slotSelectionNoteMobile: meeting.slotSelectionNoteMobile,
        slotSelectionClosed: meeting.slotSelectionClosed,
        slotSelectionErrorText: meeting.slotSelectionErrorText,
        showFormOnError: meeting.showFormOnError,
        noSlotsHeading: meeting.noSlotsHeading,
        noSlotsText: meeting.noSlotsText,
        showNoSuitableTime: meeting.showNoSuitableTime,
        noSuitableTimeLinkLabel: meeting.noSuitableTimeLinkLabel,
        noSuitableTimeHeading: meeting.noSuitableTimeHeading,
        noSuitableTimeText: meeting.noSuitableTimeText,
        formHeading: meeting.formHeading,
        formPreamble: meeting.formPreamble,
        hideNameField: meeting.hideNameField,
        formName: meeting.formName,
        formEmail: meeting.formEmail,
        showAddressField: meeting.showAddressField,
        formAddress: meeting.formAddress,
        formPhone: meeting.formPhone,
        showTextareaField: meeting.showTextareaField,
        formTextarea: meeting.formTextarea,
        formTextareaPlaceholder: meeting.formTextareaPlaceholder,
        formOptin: meeting.formOptin,
        formRequiredFields: meeting.formRequiredFields,
        formExtraInformation: meeting.formExtraInformation,
        formSubmitButtonText: meeting.formSubmitButtonText,
        questions: meeting.questions,
        preparationHeading: meeting.preparationHeading,
        preparationText: meeting.preparationText,
        preparationHeadingReturning: meeting.preparationHeadingReturning,
        preparationHeadingColoredText: meeting.preparationHeadingColoredText,
        preparationHeadingColoredTextReturning:
          meeting.preparationHeadingColoredTextReturning,
        preparationBookingCompleteHeading:
          meeting.preparationBookingCompleteHeading,
        preparationBookingCompleteSubheading:
          meeting.preparationBookingCompleteSubheading,
        preparationTextReturning: meeting.preparationTextReturning,
        preparationDesignerHeading: meeting.preparationDesignerHeading,
        preparationDesignerText: meeting.preparationDesignerText,
        preparationDesignerHeadingReturning:
          meeting.preparationDesignerHeadingReturning,
        preparationDesignerTextReturning:
          meeting.preparationDesignerTextReturning,
        preparationDesignerImage: meeting.preparationDesignerImage,
        preparationDesignerName: meeting.preparationDesignerName,
        preparationDesignerLocation: meeting.preparationDesignerLocation,
        confirmationHeading: meeting.confirmationHeading,
        confirmationText: meeting.confirmationText,
        confirmationHeadingReturning: meeting.confirmationHeadingReturning,
        confirmationTextReturning: meeting.confirmationTextReturning,
        confirmationInfo: meeting.confirmationInfo,
        confirmationDesignerHeading: meeting.confirmationDesignerHeading,
        confirmationDesignerText: meeting.confirmationDesignerText,
        confirmationButtonText: meeting.confirmationButtonText,
        confirmationButtonLink: meeting.confirmationButtonLink,
        confirmationDesignerHeadingReturning:
          meeting.confirmationDesignerHeadingReturning,
        confirmationDesignerTextReturning:
          meeting.confirmationDesignerTextReturning,
        confirmationButtonTextReturning:
          meeting.confirmationButtonTextReturning,
        confirmationButtonLinkReturning:
          meeting.confirmationButtonLinkReturning,
        confirmationChangeBookingHeading:
          meeting.confirmationChangeBookingHeading,
        confirmationChangeBookingText: meeting.confirmationChangeBookingText,
        isFollowUpMeeting: meeting.isFollowUpMeeting,
      })) || [],
    [data?.meetingTypes]
  )
  const otherNeeds = useMemo(
    () =>
      data?.additionalMeetingTypeInformation
        ?.map((info) => ({
          heading: info.heading,
          text: info.text,
        }))
        ?.filter(({ heading, text }) => !!heading && !!text) || [],
    [data?.additionalMeetingTypeInformation]
  )

  const pageContent = useMemo(
    () => ({
      id: data?.id,
      pageHeading: data?.pageHeading,
      preambleDesktop: data?.preambleDesktop,
      preambleMobile: data?.preambleMobile,
      backgroundImageDesktop: data?.backgroundImageDesktop,
      backgroundImageMobile: data?.backgroundImageMobile,
      imagesDesktop: data?.imagesDesktop,
      imagesMobile: data?.imagesMobile,
      imagesText: data?.imagesText,
      uspsDesktop: data?.imageUspsDesktop,
      uspsMobile: data?.imageUspsMobile,
      showPromotionBanner: data?.showPromotionBanner,
      promotionBannerText: data?.promotionBannerText,
      promotionBannerColor: data?.promotionBannerColor,
      promotionBannerTextColor: data?.promotionBannerTextColor,
      showTermsAndConditions: data?.showTermsAndConditions,
      termsAndConditionsText: data?.termsAndConditionsText,
      termsAndConditionsLinkLabel: data?.termsAndConditionsLinkLabel,
      termsAndConditionsSummary: data?.termsAndConditionsSummary,
      storeSelectionHeadingDesktop: data?.storeSelectionHeadingDesktop,
      storeSelectionHeadingMobile: data?.storeSelectionHeadingMobile,
      storeSelectionPreambleDesktop: data?.storeSelectionPreambleDesktop,
      storeSelectionPreambleMobile: data?.storeSelectionPreambleMobile,
      storeSelectionHeadingSelectedDesktop:
        data?.storeSelectionHeadingSelectedDesktop,
      storeSelectionHeadingSelectedMobile:
        data?.storeSelectionHeadingSelectedMobile,
      storeSelectionNearestStoresText: data?.storeSelectionNearestStoresText,
      storeSelectionAllStoresText: data?.storeSelectionAllStoresText,
      storeSelectionChangeStoreText: data?.storeSelectionChangeStoreText,
      storeSelectionChangeStoreLinkLabel:
        data?.storeSelectionChangeStoreLinkLabel,
      storeSelectionErrorMessage: data?.storeSelectionErrorMessage,
      storeSelectionNoStores: data?.storeSelectionNoStores,
      serviceSelectionHeadingDesktop: data?.serviceSelectionHeadingDesktop,
      serviceSelectionHeadingMobile: data?.serviceSelectionHeadingMobile,
      otherNeeds,
    }),
    [data, otherNeeds]
  )

  return { pageContent, services, loading: isLoading }
}
